import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { showBadgeUnlocks } from "@/lib/badges";
import { Play, Save, Loader2, History, CheckCircle, TrendingUp } from "lucide-react";
import {
  getDiscipline,
  normalizeDiscipline,
  formatPaceKm,
  formatPace100m,
  parsePaceKmToSec,
  parsePace100mToSec,
  SPORT_REF_DEFS,
} from "@/lib/trainingDisciplines";
import { initExerciseLogs, calcSessionStats, buildSavePayload } from "@/lib/workoutLogHelpers";
import { detectPRsFromSession, LIFT_LABELS } from "@/lib/strength";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import LiftMaxesEditor from "@/components/LiftMaxesEditor";
import SportRefsEditor from "@/components/training/SportRefsEditor";
import FitnessImportPanel from "@/components/fitness/FitnessImportPanel";
import { applyPrefillToExercises } from "@/lib/fitnessImport";

const MAIN_LIFTS = ["squat", "bench", "deadlift"];

function formatVolume(kg) {
  if (!kg) return "0 kg";
  return `${Math.round(kg).toLocaleString("fr-FR")} kg`;
}

function formatDistance(m) {
  if (!m) return "0 m";
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m} m`;
}

function formatDuration(sec) {
  if (!sec) return "—";
  const s = parseInt(sec, 10);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function paceDisplay(sec, modality) {
  if (!sec) return "";
  return modality === "swimming" ? formatPace100m(sec).replace("/100m", "") : formatPaceKm(sec).replace("/km", "");
}

function SetRow({ ex, exIdx, set, setIdx, updateSet, updateSetFields }) {
  const mod = ex.modality || "strength";

  const targetBadge = () => {
    if (mod === "strength" && set.target_weight_kg != null) {
      return (
        <span className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">
          {set.target_weight_kg} kg
        </span>
      );
    }
    if (mod === "running" && set.target_pace_sec_per_km) {
      return (
        <span className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">
          {formatPaceKm(set.target_pace_sec_per_km)}
        </span>
      );
    }
    if (mod === "cycling" && set.target_power_w) {
      return (
        <span className="text-xs text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded font-medium">
          {set.target_power_w} W
        </span>
      );
    }
    return null;
  };

  const completed = (
    <div className="flex items-center gap-1.5 ml-auto sm:ml-0">
      <Checkbox checked={set.completed} onCheckedChange={(v) => updateSet(exIdx, setIdx, "completed", !!v)} />
      <span className="text-xs text-slate-500">Fait</span>
    </div>
  );

  if (mod === "running") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-slate-400 w-12">Série {set.set_number}</span>
        {targetBadge()}
        <Input
          type="number"
          placeholder="m"
          value={set.distance_m}
          onChange={(e) => updateSet(exIdx, setIdx, "distance_m", e.target.value)}
          className="w-24 h-8 text-sm"
        />
        <Input
          placeholder="4:30"
          value={set.pace_input ?? paceDisplay(set.pace_sec_per_km, mod)}
          onChange={(e) => {
            const val = e.target.value;
            const sec = parsePaceKmToSec(val);
            updateSetFields(exIdx, setIdx, { pace_input: val, ...(sec ? { pace_sec_per_km: sec } : {}) });
          }}
          className="w-20 h-8 text-sm"
          title="Allure min/km"
        />
        <Input
          type="number"
          step="0.5"
          placeholder="RPE"
          value={set.rpe}
          onChange={(e) => updateSet(exIdx, setIdx, "rpe", e.target.value)}
          className="w-14 h-8 text-sm"
        />
        {set.last_distance_m != null && (
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            Dernier : {formatDistance(set.last_distance_m)}
            {set.last_pace_sec_per_km ? ` @ ${formatPaceKm(set.last_pace_sec_per_km)}` : ""}
          </span>
        )}
        {completed}
      </div>
    );
  }

  if (mod === "cycling") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-slate-400 w-12">Bloc {set.set_number}</span>
        {targetBadge()}
        <Input
          type="number"
          placeholder="W"
          value={set.power_w}
          onChange={(e) => updateSet(exIdx, setIdx, "power_w", e.target.value)}
          className="w-20 h-8 text-sm"
        />
        <Input
          type="number"
          placeholder="sec"
          value={set.duration_sec}
          onChange={(e) => updateSet(exIdx, setIdx, "duration_sec", e.target.value)}
          className="w-20 h-8 text-sm"
        />
        <Input
          type="number"
          placeholder="m"
          value={set.distance_m}
          onChange={(e) => updateSet(exIdx, setIdx, "distance_m", e.target.value)}
          className="w-24 h-8 text-sm"
        />
        <Input
          type="number"
          step="0.5"
          placeholder="RPE"
          value={set.rpe}
          onChange={(e) => updateSet(exIdx, setIdx, "rpe", e.target.value)}
          className="w-14 h-8 text-sm"
        />
        {set.last_power_w != null && (
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            Dernier : {set.last_power_w} W
          </span>
        )}
        {completed}
      </div>
    );
  }

  if (mod === "swimming") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-slate-400 w-12">Série {set.set_number}</span>
        <Input
          type="number"
          placeholder="m"
          value={set.distance_m}
          onChange={(e) => updateSet(exIdx, setIdx, "distance_m", e.target.value)}
          className="w-24 h-8 text-sm"
        />
        <Input
          placeholder="1:35"
          value={set.pace_input ?? paceDisplay(set.pace_sec_per_km, mod)}
          onChange={(e) => {
            const val = e.target.value;
            const sec = parsePace100mToSec(val);
            updateSetFields(exIdx, setIdx, { pace_input: val, ...(sec ? { pace_sec_per_km: sec } : {}) });
          }}
          className="w-20 h-8 text-sm"
          title="/100m"
        />
        <Input
          type="number"
          placeholder="sec"
          value={set.duration_sec}
          onChange={(e) => updateSet(exIdx, setIdx, "duration_sec", e.target.value)}
          className="w-20 h-8 text-sm"
        />
        <Input
          type="number"
          step="0.5"
          placeholder="RPE"
          value={set.rpe}
          onChange={(e) => updateSet(exIdx, setIdx, "rpe", e.target.value)}
          className="w-14 h-8 text-sm"
        />
        {set.last_duration_sec != null && (
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            Dernier : {formatDuration(set.last_duration_sec)}
          </span>
        )}
        {completed}
      </div>
    );
  }

  if (mod === "general") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-slate-400 w-12">Bloc {set.set_number}</span>
        <Input
          type="number"
          placeholder="sec"
          value={set.duration_sec}
          onChange={(e) => updateSet(exIdx, setIdx, "duration_sec", e.target.value)}
          className="w-20 h-8 text-sm"
        />
        <Input
          placeholder="Notes"
          value={set.notes}
          onChange={(e) => updateSet(exIdx, setIdx, "notes", e.target.value)}
          className="flex-1 min-w-[120px] h-8 text-sm"
        />
        <Input
          type="number"
          step="0.5"
          placeholder="RPE"
          value={set.rpe}
          onChange={(e) => updateSet(exIdx, setIdx, "rpe", e.target.value)}
          className="w-14 h-8 text-sm"
        />
        {completed}
      </div>
    );
  }

  // strength (default)
  const vol = set.completed && set.weight_kg && set.reps ? parseFloat(set.weight_kg) * parseInt(set.reps, 10) : 0;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-slate-400 w-12">Série {set.set_number}</span>
      {targetBadge()}
      <Input
        type="number"
        placeholder="kg"
        value={set.weight_kg}
        onChange={(e) => updateSet(exIdx, setIdx, "weight_kg", e.target.value)}
        className="w-20 h-8 text-sm"
      />
      <span className="text-xs text-slate-400">×</span>
      <Input
        type="number"
        placeholder="reps"
        value={set.reps}
        onChange={(e) => updateSet(exIdx, setIdx, "reps", e.target.value)}
        className="w-16 h-8 text-sm"
      />
      <Input
        type="number"
        step="0.5"
        placeholder="RPE"
        value={set.rpe}
        onChange={(e) => updateSet(exIdx, setIdx, "rpe", e.target.value)}
        className="w-14 h-8 text-sm"
      />
      {(set.last_weight_kg != null || set.last_reps != null) && (
        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
          Dernier : {set.last_weight_kg ?? "—"} kg × {set.last_reps ?? "—"}
        </span>
      )}
      {vol > 0 && (
        <span className="text-xs text-orange-600 font-medium">= {formatVolume(vol)}</span>
      )}
      {completed}
    </div>
  );
}

function formatSetDetail(s, modality) {
  if (modality === "running") {
    const parts = [];
    if (s.distance_m) parts.push(formatDistance(s.distance_m));
    if (s.pace_sec_per_km) parts.push(formatPaceKm(s.pace_sec_per_km));
    return parts.join(" · ") || "—";
  }
  if (modality === "cycling") {
    const parts = [];
    if (s.power_w) parts.push(`${s.power_w} W`);
    if (s.duration_sec) parts.push(formatDuration(s.duration_sec));
    if (s.distance_m) parts.push(formatDistance(s.distance_m));
    return parts.join(" · ") || "—";
  }
  if (modality === "swimming") {
    const parts = [];
    if (s.distance_m) parts.push(`${s.distance_m} m`);
    if (s.pace_sec_per_km) parts.push(formatPace100m(s.pace_sec_per_km));
    if (s.duration_sec) parts.push(formatDuration(s.duration_sec));
    return parts.join(" · ") || "—";
  }
  if (modality === "general") {
    const parts = [];
    if (s.duration_sec) parts.push(formatDuration(s.duration_sec));
    if (s.notes) parts.push(s.notes);
    return parts.join(" · ") || "—";
  }
  return `${s.weight_kg ?? "—"} kg × ${s.reps ?? "—"} reps`;
}

export default function WorkoutLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fitnessQueryStatus = searchParams.get("fitness");
  const today = new Date().toISOString().split("T")[0];
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [exercises, setExercises] = useState([]);
  const [lastSessionInfo, setLastSessionInfo] = useState(null);
  const [notes, setNotes] = useState("");
  const [duration, setDuration] = useState("");
  const [sessionDate, setSessionDate] = useState(today);
  const [detailSession, setDetailSession] = useState(null);
  const [liftMaxes, setLiftMaxes] = useState([]);
  const [sportRefs, setSportRefs] = useState([]);
  const [externalProvider, setExternalProvider] = useState(null);
  const [externalActivityId, setExternalActivityId] = useState(null);

  const clearFitnessQuery = useCallback(() => {
    if (!searchParams.get("fitness")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("fitness");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleImportPrefill = useCallback((prefill) => {
    if (prefill.session_date) setSessionDate(prefill.session_date);
    if (prefill.duration_min) setDuration(String(prefill.duration_min));
    if (prefill.notes_append) {
      setNotes((prev) => (prev ? `${prev}\n${prefill.notes_append}` : prefill.notes_append));
    }
    if (prefill.external_provider) setExternalProvider(prefill.external_provider);
    if (prefill.external_activity_id) setExternalActivityId(prefill.external_activity_id);
    setExercises((prev) => applyPrefillToExercises(prev, prefill));
  }, []);

  const currentPlan = plans.find((p) => p.plan_id === selectedPlan);
  const planDiscipline = normalizeDiscipline(currentPlan?.discipline || "strength");
  const disciplineMeta = getDiscipline(planDiscipline);
  const DisciplineIcon = disciplineMeta.icon;
  const days = currentPlan?.days || [];

  const { totalVolume, totalDistance, completedSets } = useMemo(
    () => calcSessionStats(exercises, planDiscipline),
    [exercises, planDiscipline]
  );

  const showVolume = planDiscipline === "strength" || planDiscipline === "hybrid";
  const showDistance = ["running", "cycling", "swimming", "hybrid"].includes(planDiscipline);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, histRes, maxesRes, refsRes] = await Promise.all([
        api.get("/training-plans"),
        api.get("/workouts"),
        api.get("/lift-maxes").catch(() => ({ data: [] })),
        api.get("/sport-refs").catch(() => ({ data: [] })),
      ]);
      setPlans(plansRes.data || []);
      setHistory(histRes.data || []);
      setLiftMaxes(maxesRes.data || []);
      setSportRefs(refsRes.data || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePlanChange = (planId) => {
    setSelectedPlan(planId);
    setSelectedDay("");
    setExercises([]);
    setLastSessionInfo(null);
  };

  const handleDayChange = async (dayName) => {
    setSelectedDay(dayName);
    const day = days.find((d) => d.day === dayName);
    if (!day?.exercises) {
      setExercises([]);
      setLastSessionInfo(null);
      return;
    }

    setLoadingDay(true);
    let lastByName = {};
    let lastInfo = null;
    const ctx = { liftMaxes, sportRefs };

    try {
      const { data } = await api.get("/workouts/last", {
        params: { plan_id: selectedPlan || undefined, day_name: dayName },
      });
      if (data?.session_date) {
        lastInfo = {
          date: data.session_date,
          volume: data.total_volume_kg,
          distance: data.total_distance_m,
          discipline: data.discipline,
        };
        (data.exercises || []).forEach((ex) => {
          lastByName[ex.name] = ex;
        });
      }
    } catch {
      /* pas de séance précédente */
    }

    setLastSessionInfo(lastInfo);
    setExercises(day.exercises.map((ex, i) => initExerciseLogs(ex, i, planDiscipline, lastByName[ex.name], ctx)));
    setLoadingDay(false);
  };

  const updateSet = (exIdx, setIdx, field, value) => {
    setExercises((prev) =>
      prev.map((ex, i) => {
        if (i !== exIdx) return ex;
        return {
          ...ex,
          sets: ex.sets.map((s, j) => (j === setIdx ? { ...s, [field]: value } : s)),
        };
      })
    );
  };

  const updateSetFields = (exIdx, setIdx, fields) => {
    setExercises((prev) =>
      prev.map((ex, i) => {
        if (i !== exIdx) return ex;
        return {
          ...ex,
          sets: ex.sets.map((s, j) => (j === setIdx ? { ...s, ...fields } : s)),
        };
      })
    );
  };

  const addSet = (exIdx) => {
    setExercises((prev) =>
      prev.map((ex, i) => {
        if (i !== exIdx) return ex;
        const last = ex.sets[ex.sets.length - 1];
        const mod = ex.modality || planDiscipline;
        const base = {
          set_number: ex.sets.length + 1,
          completed: false,
          rpe: "",
          target_weight_kg: last?.target_weight_kg ?? null,
          target_pct: last?.target_pct ?? null,
          target_pace_sec_per_km: last?.target_pace_sec_per_km ?? null,
          target_power_w: last?.target_power_w ?? null,
        };

        if (mod === "strength") {
          return {
            ...ex,
            sets: [
              ...ex.sets,
              {
                ...base,
                weight_kg: last?.weight_kg || (last?.target_weight_kg ? String(last.target_weight_kg) : ""),
                reps: last?.reps || 10,
                last_weight_kg: last?.last_weight_kg ?? last?.weight_kg ?? null,
                last_reps: last?.last_reps ?? last?.reps ?? null,
              },
            ],
          };
        }
        if (mod === "running") {
          return {
            ...ex,
            sets: [
              ...ex.sets,
              {
                ...base,
                distance_m: last?.distance_m ?? "",
                pace_sec_per_km: last?.pace_sec_per_km ?? "",
                last_distance_m: last?.distance_m ?? null,
                last_pace_sec_per_km: last?.pace_sec_per_km ?? null,
              },
            ],
          };
        }
        if (mod === "cycling") {
          return {
            ...ex,
            sets: [
              ...ex.sets,
              {
                ...base,
                duration_sec: last?.duration_sec ?? "",
                power_w: last?.power_w ?? "",
                distance_m: last?.distance_m ?? "",
                last_power_w: last?.power_w ?? null,
              },
            ],
          };
        }
        if (mod === "swimming") {
          return {
            ...ex,
            sets: [
              ...ex.sets,
              {
                ...base,
                distance_m: last?.distance_m ?? "",
                duration_sec: last?.duration_sec ?? "",
                pace_sec_per_km: last?.pace_sec_per_km ?? "",
                last_duration_sec: last?.duration_sec ?? null,
              },
            ],
          };
        }
        return {
          ...ex,
          sets: [
            ...ex.sets,
            {
              ...base,
              duration_sec: last?.duration_sec ?? "",
              notes: last?.notes ?? "",
            },
          ],
        };
      })
    );
  };

  const applyPRUpdates = async (prs) => {
    const maxes = MAIN_LIFTS.map((lift) => {
      const pr = prs.find((p) => p.lift === lift);
      const existing = liftMaxes.find((m) => m.lift === lift);
      return {
        lift,
        value_kg: pr ? pr.weight_kg : existing?.value_kg ?? null,
        reference_type: pr?.reference_type || existing?.reference_type || "tm",
      };
    }).filter((m) => m.value_kg);

    try {
      const { data } = await api.put("/lift-maxes", { maxes });
      setLiftMaxes(data || []);
      toast.success("Records personnels mis à jour !");
    } catch {
      toast.error("Impossible de mettre à jour les max");
    }
  };

  const handleSave = async () => {
    if (!selectedDay || exercises.length === 0) {
      toast.error("Sélectionnez un jour d'entraînement");
      return;
    }
    if (completedSets === 0) {
      toast.error("Cochez au moins une série réalisée");
      return;
    }
    setSaving(true);

    const isStrengthSession = planDiscipline === "strength" || planDiscipline === "hybrid";
    const prs = isStrengthSession ? detectPRsFromSession(exercises, liftMaxes) : [];

    const payload = buildSavePayload({
      selectedPlan,
      selectedDay,
      sessionDate,
      notes,
      duration,
      exercises,
      planDiscipline,
      externalProvider,
      externalActivityId,
    });

    try {
      const res = await api.post("/workouts", payload);
      showBadgeUnlocks(res.data?.new_badges, toast);

      const summaryParts = [];
      if (showVolume && totalVolume > 0) summaryParts.push(formatVolume(totalVolume));
      if (showDistance && totalDistance > 0) summaryParts.push(formatDistance(totalDistance));
      toast.success(`Séance enregistrée${summaryParts.length ? ` — ${summaryParts.join(" · ")}` : ""}`);

      for (const pr of prs) {
        const label = LIFT_LABELS[pr.lift] || pr.lift;
        const delta = pr.previous_kg ? ` (+${(pr.weight_kg - pr.previous_kg).toFixed(1)} kg)` : "";
        toast.success(`Nouveau PR ${label} : ${pr.weight_kg} kg${delta}`, {
          duration: 12000,
          action: {
            label: "Mettre à jour mon max",
            onClick: () => applyPRUpdates([pr]),
          },
        });
      }

      setNotes("");
      setDuration("");
      setExercises([]);
      setSelectedDay("");
      setLastSessionInfo(null);
      setExternalProvider(null);
      setExternalActivityId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const viewSession = async (id) => {
    try {
      const { data } = await api.get(`/workouts/${id}`);
      setDetailSession(data);
    } catch {
      toast.error("Erreur");
    }
  };

  const saveLabel = () => {
    const parts = [];
    if (showVolume && totalVolume > 0) parts.push(formatVolume(totalVolume));
    if (showDistance && totalDistance > 0) parts.push(formatDistance(totalDistance));
    return parts.length ? parts.join(" · ") : "Séance";
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div data-testid="workout-log-page">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${disciplineMeta.light}`}>
          <DisciplineIcon className={`w-6 h-6 ${disciplineMeta.accent}`} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
              Suivi d&apos;Entraînement
            </h1>
            {currentPlan && <DisciplineBadge discipline={planDiscipline} size="sm" />}
          </div>
          <p className="text-slate-400 mt-1 text-sm">
            {currentPlan ? disciplineMeta.logLabel : "Enregistrez vos performances"}
          </p>
        </div>
      </div>

      {disciplineMeta.usesLiftMaxes && (
        <Card className="mt-6 border-slate-200 bg-slate-50/80">
          <CardContent className="p-4">
            <LiftMaxesEditor compact onSaved={(data) => setLiftMaxes(data || [])} />
          </CardContent>
        </Card>
      )}

      {disciplineMeta.usesSportRefs && (
        <Card className="mt-4 border-cyan-100 bg-cyan-50/30">
          <CardContent className="p-4">
            <SportRefsEditor compact onSaved={(data) => setSportRefs(data || [])} />
          </CardContent>
        </Card>
      )}

      <FitnessImportPanel
        selectedDay={selectedDay}
        exercises={exercises}
        onImportPrefill={handleImportPrefill}
        fitnessQueryStatus={fitnessQueryStatus}
        onClearFitnessQuery={clearFitnessQuery}
      />

      <Tabs defaultValue="log" className="mt-6">
        <TabsList>
          <TabsTrigger value="log" className="gap-1.5">
            <Play className="w-4 h-4" /> Nouvelle séance
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5">
            <History className="w-4 h-4" /> Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-4">
          {plans.length === 0 ? (
            <Card className="border-slate-100">
              <CardContent className="p-8 text-center text-slate-500">
                Aucun programme assigné. Demandez un plan à votre coach.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Programme</label>
                  <Select value={selectedPlan} onValueChange={handlePlanChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.plan_id} value={p.plan_id}>
                          {p.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Jour</label>
                  <Select value={selectedDay} onValueChange={handleDayChange} disabled={!selectedPlan}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir le jour" />
                    </SelectTrigger>
                    <SelectContent>
                      {days.map((d) => (
                        <SelectItem key={d.day} value={d.day}>
                          {d.day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Date</label>
                  <Input type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} />
                </div>
              </div>

              {disciplineMeta.usesLiftMaxes && liftMaxes.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {liftMaxes.map((m) => (
                    <Badge key={m.lift} variant="outline" className="bg-slate-50">
                      {LIFT_LABELS[m.lift] || m.lift}: {m.value_kg} kg ({m.reference_type === "1rm" ? "1RM" : "TM"})
                    </Badge>
                  ))}
                </div>
              )}

              {disciplineMeta.usesSportRefs && sportRefs.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {sportRefs.map((r) => {
                    const def = SPORT_REF_DEFS[r.ref_key];
                    let display = r.value;
                    if (r.ref_key === "threshold_pace_sec_km") display = formatPaceKm(r.value);
                    else if (r.ref_key === "css_sec_100m") display = formatPace100m(r.value);
                    return (
                      <Badge key={r.ref_key} variant="outline" className={`${disciplineMeta.light} border-0`}>
                        {def?.label || r.ref_key}: {display}{def?.unit && r.ref_key !== "threshold_pace_sec_km" && r.ref_key !== "css_sec_100m" ? ` ${def.unit}` : ""}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {loadingDay && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement de votre dernière séance...
                </div>
              )}

              {lastSessionInfo && !loadingDay && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-800">
                  <TrendingUp className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>
                    Dernière fois le{" "}
                    <strong>{new Date(lastSessionInfo.date).toLocaleDateString("fr-FR")}</strong>
                    {lastSessionInfo.volume ? (
                      <> — volume : <strong>{formatVolume(lastSessionInfo.volume)}</strong></>
                    ) : null}
                    {lastSessionInfo.distance ? (
                      <> — distance : <strong>{formatDistance(lastSessionInfo.distance)}</strong></>
                    ) : null}
                    . Les valeurs précédentes sont affichées sous chaque série.
                  </p>
                </div>
              )}

              {exercises.length > 0 && !loadingDay && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Durée (min)</label>
                      <Input
                        type="number"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        placeholder="60"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Notes</label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={1}
                        placeholder="Ressenti, fatigue..."
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex-wrap gap-3">
                    <div>
                      <p className="text-xs text-orange-600 font-medium uppercase tracking-wide">
                        {showVolume && showDistance ? "Volume & distance" : showDistance ? "Distance totale" : "Volume total"}
                      </p>
                      <div className="flex items-baseline gap-3 mt-1">
                        {showVolume && (
                          <p className="text-2xl font-bold text-orange-700">{formatVolume(totalVolume)}</p>
                        )}
                        {showDistance && (
                          <p className={`font-bold text-orange-700 ${showVolume ? "text-lg" : "text-2xl"}`}>
                            {formatDistance(totalDistance)}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-orange-500">{completedSets} série(s) cochée(s)</p>
                    </div>
                    {lastSessionInfo && (lastSessionInfo.volume || lastSessionInfo.distance) && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Dernière séance</p>
                        {lastSessionInfo.volume != null && showVolume && (
                          <p className="text-lg font-semibold text-slate-700">{formatVolume(lastSessionInfo.volume)}</p>
                        )}
                        {lastSessionInfo.distance != null && showDistance && (
                          <p className="text-lg font-semibold text-slate-700">{formatDistance(lastSessionInfo.distance)}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {exercises.map((ex, exIdx) => (
                      <Card key={exIdx} className="border-orange-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <div>
                              <h3 className="font-semibold text-slate-900">{ex.name}</h3>
                              {ex.prescription && (
                                <p className="text-xs text-blue-600 mt-0.5">Prescription : {ex.prescription}</p>
                              )}
                            </div>
                            {ex.modality && ex.modality !== planDiscipline && (
                              <DisciplineBadge discipline={ex.modality} size="sm" />
                            )}
                          </div>
                          <div className="space-y-2">
                            {ex.sets.map((set, setIdx) => (
                              <SetRow
                                key={setIdx}
                                ex={ex}
                                exIdx={exIdx}
                                set={set}
                                setIdx={setIdx}
                                updateSet={updateSet}
                                updateSetFields={updateSetFields}
                              />
                            ))}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => addSet(exIdx)}
                            className="mt-2 text-xs text-orange-600"
                          >
                            + Ajouter une série
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={saving || completedSets === 0}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full h-11 gap-2"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" /> Enregistrer — {saveLabel()}
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <p className="text-center text-slate-400 py-12">Aucune séance enregistrée</p>
          ) : (
            <div className="space-y-3">
              {history.map((s) => {
                const sessionDisc = normalizeDiscipline(s.discipline || "strength");
                const isEndurance = ["running", "cycling", "swimming"].includes(sessionDisc);
                return (
                  <Card
                    key={s.session_id}
                    className="border-slate-100 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => viewSession(s.session_id)}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{s.day_name}</p>
                          {s.discipline && <DisciplineBadge discipline={s.discipline} size="sm" />}
                        </div>
                        <p className="text-xs text-slate-400">
                          {new Date(s.session_date).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <div className="flex gap-2 items-center">
                        {!isEndurance && s.total_volume_kg != null && (
                          <Badge variant="secondary">{formatVolume(s.total_volume_kg)}</Badge>
                        )}
                        {(isEndurance || s.total_distance_m) && s.total_distance_m != null && (
                          <Badge variant="secondary">{formatDistance(s.total_distance_m)}</Badge>
                        )}
                        {s.duration_min && <Badge variant="outline">{s.duration_min} min</Badge>}
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {detailSession && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetailSession(null)}
        >
          <Card className="max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-xl font-bold">{detailSession.day_name}</h3>
                {detailSession.discipline && <DisciplineBadge discipline={detailSession.discipline} size="sm" />}
              </div>
              <p className="text-sm text-slate-500">
                {new Date(detailSession.session_date).toLocaleDateString("fr-FR")}
              </p>
              {detailSession.total_volume_kg != null && (
                <p className="text-sm font-semibold text-orange-600 mt-1">
                  Volume : {formatVolume(detailSession.total_volume_kg)}
                </p>
              )}
              {detailSession.total_distance_m != null && (
                <p className="text-sm font-semibold text-emerald-600 mt-1">
                  Distance : {formatDistance(detailSession.total_distance_m)}
                </p>
              )}
              {detailSession.notes && (
                <p className="text-sm text-slate-600 mt-2 italic">{detailSession.notes}</p>
              )}
              <div className="mt-4 space-y-4">
                {(detailSession.exercises || []).map((ex, i) => (
                  <div key={i}>
                    <p className="font-medium text-slate-900 mb-1">{ex.name}</p>
                    {ex.sets.map((s, j) => (
                      <p key={j} className="text-sm text-slate-500">
                        S{s.set_number}: {formatSetDetail(s, ex.modality || detailSession.discipline)}
                        {s.rpe ? ` RPE ${s.rpe}` : ""} {s.completed ? "✓" : "—"}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
              <Button className="mt-4 w-full" variant="outline" onClick={() => setDetailSession(null)}>
                Fermer
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}